import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagsEditor } from "@/shared/components/TagsEditor";
import { type SkillSummary } from "@/shared/domain/skills";
import { useUpdateSkillMetadata } from "@/shared/domain/skills/hooks";
import { Dialog, RobotoButton } from "@/shared/foundation/primitives";

import styles from "./SkillTagsDialog.module.css";

interface SkillTagsDialogProps {
  open: boolean;
  onClose: () => void;
  /** The skill being edited. `null` means the dialog is closed. */
  summary: SkillSummary | null;
  orgId: string | undefined;
}

/**
 * Edits a skill's tags from the skills list, in the same editor as the
 * skill's own page: each tag saves as it is added or removed, and the editor
 * says itself when a save fails. The list refetches after each save, so the
 * tags shown here are the skill's saved ones.
 */
export function SkillTagsDialog({
  open,
  onClose,
  summary,
  orgId,
}: SkillTagsDialogProps) {
  const updateMetadata = useUpdateSkillMetadata(orgId);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content className={styles.content}>
        <Dialog.Title>
          {summary === null ? "Edit tags" : `Edit tags — ${summary.skill.name}`}
        </Dialog.Title>
        <Dialog.Description>
          Free-form labels for grouping skills. Each change saves as you make
          it.
        </Dialog.Description>
        {summary !== null && (
          <div className={styles.editor}>
            <TagsEditor
              tags={summary.skill.tags}
              recordKey={summary.skill.skill_id}
              variant="field"
              autocompleteType={AutocompleteType.SkillTags}
              onAdd={(tag) =>
                updateMetadata.mutateAsync({
                  skillId: summary.skill.skill_id,
                  request: { put_tags: [tag] },
                })
              }
              onRemove={(tag) =>
                updateMetadata.mutateAsync({
                  skillId: summary.skill.skill_id,
                  request: { remove_tags: [tag] },
                })
              }
            />
          </div>
        )}
        <div className={styles.actions}>
          <Dialog.Close
            render={<RobotoButton variant="secondary">Done</RobotoButton>}
          />
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
